// =====================================================
// TRADER DISCIPLINE INDEX (TDI) - SERVICE
// Calculates and retrieves discipline scores
// =====================================================

import { supabase } from '@/integrations/supabase/client';
import type { TDIScore, TDIHistory } from '@/types/disciplineScore';

/**
 * Calculate TDI v2 score for the current user
 * Uses BOTH journal_entries AND counterfactual_tracking data
 * TDI_final = 0.6 * BTDI + 0.4 * CETDI
 */
export async function calculateTDIScore(days: number = 30): Promise<TDIScore> {
  try {
    // Use v2 function that includes counterfactual data
    const { data, error } = await supabase.rpc('calculate_tdi_score_v2', {
      p_days: days,
    });

    if (error) {
      console.error('[DisciplineScoreService] Error calculating TDI v2:', error);
      // Fallback to v1 if v2 fails
      const { data: v1Data, error: v1Error } = await supabase.rpc('calculate_tdi_score', {
        p_days: days,
      });
      if (v1Error) {
        return { success: false, tdi_score: null, error: v1Error.message };
      }
      return v1Data as TDIScore;
    }

    return data as TDIScore;
  } catch (err) {
    console.error('[DisciplineScoreService] Exception calculating TDI:', err);
    return { 
      success: false, 
      tdi_score: null, 
      error: err instanceof Error ? err.message : 'Unknown error' 
    };
  }
}

/**
 * Get TDI history for the current user
 */
export async function getTDIHistory(days: number = 30): Promise<TDIHistory> {
  try {
    const { data, error } = await supabase.rpc('get_tdi_history', {
      p_days: days,
    });

    if (error) {
      console.error('[DisciplineScoreService] Error getting TDI history:', error);
      return { 
        success: false, 
        history: [], 
        stats: { avg_score: null, high_score: null, low_score: null, current_score: null, total_entries: 0 },
        error: error.message 
      };
    }

    return data as TDIHistory;
  } catch (err) {
    console.error('[DisciplineScoreService] Exception getting TDI history:', err);
    return { 
      success: false, 
      history: [], 
      stats: { avg_score: null, high_score: null, low_score: null, current_score: null, total_entries: 0 },
      error: err instanceof Error ? err.message : 'Unknown error' 
    };
  }
}

/**
 * Get the latest TDI score without recalculating
 */
export async function getLatestTDIScore(): Promise<TDIScore | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('discipline_scores')
      .select('*')
      .eq('user_id', user.id)
      .order('score_date', { ascending: false })
      .limit(1)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No data found
        return null;
      }
      console.error('[DisciplineScoreService] Error getting latest TDI:', error);
      return null;
    }

    // Convert to TDIScore format
    return {
      success: true,
      tdi_score: data.tdi_score,
      tier: data.tdi_score >= 750 ? 'EXCELLENT' : 
            data.tdi_score >= 650 ? 'GOOD' : 
            data.tdi_score >= 550 ? 'FAIR' : 'DEVELOPING',
      trend: data.trend as TDIScore['trend'],
      trend_change: data.trend_change,
      components: {
        capital_preservation: data.capital_preservation_score,
        decision_consistency: data.decision_consistency_score,
        risk_avoidance: data.risk_avoidance_score,
        behavioral_stability: data.behavioral_stability_score,
      },
      factors: data.score_factors as TDIScore['factors'],
      decisions_counted: data.decisions_counted,
    };
  } catch (err) {
    console.error('[DisciplineScoreService] Exception getting latest TDI:', err);
    return null;
  }
}
